import {Flag, Tooltip} from 'platform/components';
import {Box, HStack} from 'platform/foundation';

import {isNil} from 'ramda';

import {CellRendererProps} from '../../types/CellRendererProps';
import {TooltipGetter} from '../../types/TooltipGetter';
import {getItemTooltipProps} from '../../utils/getItemTooltipProps';
import {getTooltipProps} from '../../utils/getTooltipProps';
import {EmptyCellValue} from '../EmptyCellValue/EmptyCellValue';

// API shape: row[key].value is an array of these
type TagValue = {
  label: string;
  style?: {flagColor?: string; flagSubtle?: boolean};
} | null;

type TagCellRendererProps = CellRendererProps<Array<TagValue> | TagValue> & {
  getTooltipProps?: TooltipGetter;
};

const getFlagProps = (tagValue: NonNullable<TagValue>, label: string) => ({
  colorScheme: tagValue.style?.flagColor as never,
  isSubtle: tagValue.style?.flagSubtle ?? false,
  label,
  'data-testid': `tag-cell-renderer-${label}`,
});

export function TagCellRenderer(props: TagCellRendererProps) {
  const tooltipProps = getTooltipProps(props.row, props.getTooltipProps);

  if (isNil(props.value)) {
    return <EmptyCellValue emptyCellValue={props.emptyCellValue} isWrap={props.isWrap} />;
  }

  if (!Array.isArray(props.value)) {
    const singleValue = props.value as TagValue;
    if (isNil(singleValue)) {
      return <EmptyCellValue emptyCellValue={props.emptyCellValue} isWrap={props.isWrap} />;
    }
    return (
      <Tooltip {...tooltipProps}>
        <Flag {...getFlagProps(singleValue, singleValue.label)} />
      </Tooltip>
    );
  }

  if (props.value.length === 0) {
    return <EmptyCellValue emptyCellValue={props.emptyCellValue} isWrap={props.isWrap} />;
  }

  return (
    <Box paddingVertical={1} paddingRight={1}>
      <HStack spacing={1} wrap={props.isWrap}>
        {props.value.map((item, index) => {
          if (isNil(item)) {
            return null;
          }
          return (
            // eslint-disable-next-line react/no-array-index-key -- Pre-existing violation
            <Tooltip {...getItemTooltipProps(index, tooltipProps)} key={index}>
              <Flag {...getFlagProps(item, item.label)} />
            </Tooltip>
          );
        })}
      </HStack>
    </Box>
  );
}
